import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  ApiResponse,
  CreateApiResult,
  CursorPaginatedResponse,
  Invoice,
  InvoiceListData,
  InvoiceListParams,
  InvoicePayload,
} from '@/libs/interfaces'
import { invoicesApiMock } from '@/libs/mocks/handlers/billing'

const invoicesApiReal = {
  create: async (
    params: { orgID: string; storeID: string },
    payload: InvoicePayload,
  ): Promise<CreateApiResult<Invoice>> => {
    // POST responds with the created bill object directly, same as GET by ID.
    return api
      .post<Invoice>(URLs.Bills.create(params.orgID, params.storeID), payload)
      .then((response) => ({
        data: response.data,
        status: response.status,
        message: response.message,
      }))
  },

  getByID: async (params: { orgID: string; storeID: string; billID: string }): Promise<Invoice> => {
    return api
      .get<Invoice>(URLs.Bills.getByID(params.storeID, params.billID))
      .then((response) => response.data)
  },

  list: async (params: InvoiceListParams): Promise<Invoice[]> => {
    const { storeID, search, limit, cursor, prev, sortBy, sortDirection } = params
    const query = buildQueryParams({
      search,
      limit,
      cursor,
      prev,
      sortBy,
      sortDirection,
    })
    return api
      .get<ApiResponse<InvoiceListData>>(URLs.Bills.list(storeID, query))
      .then((response) => response.data?.data.bills || [])
  },

  listCursor: async (params: InvoiceListParams): Promise<CursorPaginatedResponse<Invoice>> => {
    const { storeID, search, limit, cursor, prev, sortBy, sortDirection } = params
    const query = buildQueryParams({
      search,
      limit,
      cursor,
      prev,
      sortBy,
      sortDirection,
    })
    const response = await api
      .get<ApiResponse<InvoiceListData>>(URLs.Bills.list(storeID, query))
      .then((response) => response?.data?.data)

    return {
      data: response.bills,
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },

  update: async (
    params: { orgID: string; storeID: string; billID: string },
    payload: Partial<InvoicePayload>,
  ): Promise<Invoice> => {
    return api
      .patch<Invoice>(URLs.Bills.byID(params.orgID, params.storeID, params.billID), payload)
      .then((response) => response.data)
  },

  delete: async (params: { orgID: string; storeID: string; billID: string }): Promise<Invoice> => {
    return api
      .delete<Invoice>(URLs.Bills.byID(params.orgID, params.storeID, params.billID))
      .then((response) => response.data)
  },
}

export const invoicesApi: typeof invoicesApiReal = __IS_MOCK_MODE__
  ? invoicesApiMock
  : invoicesApiReal
